import { useState, useEffect } from 'react';
import { Download, MapPin, Heart, Share2, Globe, Sparkles } from 'lucide-react';
import { ITrip } from '../../Models/trips/trip.model';
import { Row, Col } from 'react-bootstrap';

type HomePageProps = {
  onGetStarted: () => void;
  onDownloadExtension: () => void;
  onDiscoverClick?: (searchQuery?: string) => void;
};

export function HomePage({
  onGetStarted,
  onDownloadExtension,
  onDiscoverClick
}: HomePageProps) {
  const [publicTrips, setPublicTrips] = useState<ITrip[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPublicTrips();
  }, []);

  async function loadPublicTrips() {
    try {
      const publicTrips: ITrip[] = [];
      setPublicTrips(publicTrips || []);
    } catch (error) {
      console.error('Error loading public trips:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className='min-h-screen bg-white'>
      <header className='border-b border-gray-200 sticky top-0 bg-white z-10'>
        <div className='max-w-7xl mx-auto px-6 py-4 flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <h1 className='text-3xl font-serif'>
              Travel <span className='text-red-900'>Hearts</span>
            </h1>
          </div>
          <div className='flex items-center gap-3'>
            <button
              onClick={onGetStarted}
              className='px-6 py-2.5 bg-red-900 text-white rounded-full font-medium hover:bg-red-800 transition-colors'
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      <section className='max-w-6xl mx-auto px-6 py-20'>
        <div className='text-center mb-16'>
          <h2 className='text-4xl md:text-5xl font-serif text-gray-900 mb-6 leading-tight'>
            Create, Share or Discover the perfect trip
          </h2>
          <p className='text-lg text-gray-600 max-w-3xl mx-auto'>
            Save your favorite places and plan your dream vacation, or explore
            itineraries shared by travelers around the world
          </p>
        </div>

        <div className='flex justify-center'>
          <button
            onClick={onGetStarted}
            className='px-24 py-5 bg-red-900 text-white rounded-full font-bold text-2xl hover:bg-red-800 transition-all shadow-2xl hover:shadow-3xl transform hover:scale-105'
          >
            Start now
          </button>
        </div>
      </section>

      <section className='py-8'>
        <div className='max-w-7xl mx-auto px-6'>
          <div className='text-center mb-6'>
            <h3 className='text-3xl font-serif text-gray-900 mb-3'>
              Featured Public Trips
            </h3>
          </div>

          {loading ? (
            <div className='text-center py-12'>
              <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-900'></div>
            </div>
          ) : publicTrips.length > 0 ? (
            <Row className='g-4'>
              {publicTrips.map((trip) => (
                <Col
                  key={trip.id}
                  md={4}
                >
                  <div
                    className='bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer h-100'
                    onClick={onGetStarted}
                  >
                    <div className='relative h-48 bg-gray-200'>
                      {trip.imageUrl ? (
                        <img
                          src={trip.imageUrl}
                          alt={trip.title}
                          className='w-full h-full object-cover'
                          onError={(e) => {
                            e.currentTarget.src =
                              'https://images.pexels.com/photos/1008155/pexels-photo-1008155.jpeg?auto=compress&cs=tinysrgb&w=800';
                          }}
                        />
                      ) : (
                        <div className='w-full h-full flex items-center justify-center text-gray-400'>
                          No image
                        </div>
                      )}
                      <div className='absolute top-2 right-2 px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1'>
                        <Globe className='w-3 h-3' />
                        Public
                      </div>
                      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3'>
                        <h4 className='text-white font-semibold line-clamp-1'>
                          {trip.title.charAt(0).toUpperCase() +
                            trip.title.slice(1)}
                        </h4>
                      </div>
                    </div>
                    <div className='p-3'>
                      <p className='text-xs text-gray-600'>
                        By:{' '}
                        <span className='font-medium'>
                          {trip.createdByUsername}
                        </span>
                      </p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <div className='text-center py-8 text-gray-500'>
              <Globe className='w-12 h-12 mx-auto mb-3 text-gray-300' />
              <p>No public trips yet. Be the first to share your adventure!</p>
            </div>
          )}
        </div>
      </section>

      <section className='bg-gray-50 py-20'>
        <div className='max-w-7xl mx-auto px-6'>
          <h3 className='text-3xl font-serif text-center mb-12 text-gray-900'>
            Everything You Need to Plan Your Journey
          </h3>
          <Row className='g-4'>
            <Col md={4}>
              <div className='bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow h-100'>
                <div className='w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-6'>
                  <MapPin className='w-7 h-7 text-red-900' />
                </div>
                <h4 className='text-xl font-semibold mb-3 text-gray-900'>
                  Interactive Maps
                </h4>
                <p className='text-gray-600'>
                  Visualize your entire trip on a beautiful map. Pin
                  restaurants, hotels, attractions, and activities all in one
                  place.
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow h-100'>
                <div className='w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-6'>
                  <Heart className='w-7 h-7 text-red-900' />
                </div>
                <h4 className='text-xl font-semibold mb-3 text-gray-900'>
                  Save Favorite Places
                </h4>
                <p className='text-gray-600'>
                  Bookmark places you discover while browsing. Add notes, dates,
                  and organize by day for seamless trip planning.
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow h-100'>
                <div className='w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-6'>
                  <Share2 className='w-7 h-7 text-red-900' />
                </div>
                <h4 className='text-xl font-semibold mb-3 text-gray-900'>
                  Collaborate & Share
                </h4>
                <p className='text-gray-600'>
                  Invite friends and family to plan together. Share your trips
                  with a simple link and make travel planning collaborative.
                </p>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      <section className='py-20'>
        <div className='max-w-7xl mx-auto px-6'>
          <div className='bg-gradient-to-br from-red-900 to-red-800 rounded-3xl overflow-hidden'>
            <Row className='g-4 align-items-center'>
              <Col md={6}>
                <div className='p-12 text-white'>
                  <Globe className='w-16 h-16 mb-6 opacity-90' />
                  <h3 className='text-3xl font-serif mb-4'>
                    Save Places from Anywhere
                  </h3>
                  <p className='text-red-100 text-lg mb-8'>
                    Install our Chrome extension to save places directly while
                    browsing hotels, travel blogs, restaurants, activities and
                    more.
                  </p>
                  <button
                    onClick={onDownloadExtension}
                    className='px-8 py-4 bg-white text-red-900 rounded-full font-semibold hover:bg-gray-100 transition-colors text-lg shadow-lg inline-flex items-center gap-2'
                  >
                    <Download className='w-5 h-5' />
                    Download Chrome Extension
                  </button>
                </div>
              </Col>
              <Col md={6}>
                <div className='p-8'>
                  <img
                    src='https://images.unsplash.com/photo-1530841377377-3ff06c0ca713?w=800&auto=format&fit=crop&q=80'
                    alt='Travel Hearts Chrome Extension - Save Your Dream Destinations'
                    className='rounded-lg shadow-2xl w-full object-cover h-96'
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </section>

      <section className='bg-gray-50 py-20'>
        <div className='max-w-7xl mx-auto px-6'>
          <div className='text-center mb-12'>
            <Sparkles className='w-12 h-12 mx-auto mb-4 text-red-900' />
            <h3 className='text-3xl font-serif text-gray-900 mb-3'>
              Why Travel Hearts?
            </h3>
            <p className='text-gray-600 max-w-2xl mx-auto'>
              Built for travelers who want to create memorable experiences
            </p>
          </div>

          <Row className='g-4'>
            <Col md={6}>
              <div className='flex gap-4'>
                <div className='flex-shrink-0'>
                  <div className='w-10 h-10 bg-red-900 text-white rounded-full flex items-center justify-center font-semibold'>
                    ✓
                  </div>
                </div>
                <div>
                  <h4 className='font-semibold text-gray-900 mb-2'>
                    Completely Free
                  </h4>
                  <p className='text-gray-600'>
                    No hidden fees, no premium tiers. All features available to
                    everyone.
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className='flex gap-4'>
                <div className='flex-shrink-0'>
                  <div className='w-10 h-10 bg-red-900 text-white rounded-full flex items-center justify-center font-semibold'>
                    ✓
                  </div>
                </div>
                <div>
                  <h4 className='font-semibold text-gray-900 mb-2'>
                    Easy to Use
                  </h4>
                  <p className='text-gray-600'>
                    Intuitive interface designed to make trip planning
                    effortless.
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className='flex gap-4'>
                <div className='flex-shrink-0'>
                  <div className='w-10 h-10 bg-red-900 text-white rounded-full flex items-center justify-center font-semibold'>
                    ✓
                  </div>
                </div>
                <div>
                  <h4 className='font-semibold text-gray-900 mb-2'>
                    Collaborative Planning
                  </h4>
                  <p className='text-gray-600'>
                    Invite friends and family to plan trips together in
                    real-time.
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className='flex gap-4'>
                <div className='flex-shrink-0'>
                  <div className='w-10 h-10 bg-red-900 text-white rounded-full flex items-center justify-center font-semibold'>
                    ✓
                  </div>
                </div>
                <div>
                  <h4 className='font-semibold text-gray-900 mb-2'>
                    Mobile Friendly
                  </h4>
                  <p className='text-gray-600'>
                    Access your trips from any device, anywhere in the world.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      <section className='py-20 text-center'>
        <div className='max-w-3xl mx-auto px-6'>
          <h3 className='text-4xl font-serif text-gray-900 mb-6'>
            Ready to Start Planning?
          </h3>
          <p className='text-xl text-gray-600 mb-8'>
            Join travelers who are already creating amazing trips
          </p>
          <button
            onClick={onGetStarted}
            className='px-10 py-4 bg-red-900 text-white rounded-full font-semibold hover:bg-red-800 transition-colors text-lg shadow-lg hover:shadow-xl transform hover:scale-105'
          >
            Get Started Free
          </button>
        </div>
      </section>

      <footer className='border-t border-gray-200 py-8'>
        <div className='max-w-7xl mx-auto px-6 text-center text-gray-600'>
          <p className='text-sm'>© 2024 Travel Hearts. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
